import "../../../scss/lp/ci-message.scss";

import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";

gsap.registerPlugin(ScrollToPlugin);

var MESSAGE_SELECTOR = ".message";
var MESSAGE_BOX_SELECTOR = ".message__box";
var SNAP_DURATION = 0.55;
var SNAP_EASE = "power2.out";
var WHEEL_SNAP_THRESHOLD = 80;
var TOUCH_SNAP_THRESHOLD = 36;
var ENABLE_GSAP_MESSAGE_SNAP = false;
var LETTER_SELECTOR = ".letter";
var LETTER_SPLIT_SELECTOR = ".letter [data-split]";
var LETTER_SIGN_SELECTOR = ".letter__sign";
var LETTER_VISIBLE_CLASS = "is-letter-visible";
var LETTER_DIM_CLASS = "is-letter-dim";
var LETTER_DIM_LINE = 0.85;
var STATEMENT_SELECTOR = ".statement";
var STATEMENT_VISIBLE_CLASS = "is-statement-visible";
var STATEMENT_GLOW_ANCHOR_SELECTOR = ".statement__glowAnchor";
var STATEMENT_GLOW_FOLLOW = 0.55;
var STATEMENT_GLOW_EASE = 0.03;
var STATEMENT_GLOW_REST_DISTANCE = 0.1;
var SCHEDULE_SELECTOR = ".schedule";
var SCHEDULE_VISIBLE_CLASS = "is-schedule-visible";
var INK_BLEED_SELECTOR = ".js-inkBleed";
var INK_BLEED_PLAY_CLASS = "is-ink-play";
var SIGN_INK_DELAY = 500;
var HERO_BRUSH_SELECTOR = ".js-heroBrushWipe";
var HERO_BRUSH_PLAY_CLASS = "is-brush-play";
var HERO_BRUSH_LOAD_DELAY = 100; // ページロード後にブラシワイプアニメーションを再生する前の遅延

function initMessageSnap() {
	var message = document.querySelector(MESSAGE_SELECTOR);
	if (!message) return;

	var messageBoxes = Array.prototype.slice.call(message.querySelectorAll(MESSAGE_BOX_SELECTOR));
	if (messageBoxes.length <= 1) return;

	var isSnapping = false;
	var wheelAmount = 0;
	var touchStartY = null;

	function getBoxTop(messageBox) {
		return messageBox.getBoundingClientRect().top + window.pageYOffset;
	}

	function getCurrentIndex() {
		var viewportTop = window.pageYOffset;
		return messageBoxes.reduce(function (nearestIndex, messageBox, index) {
			var nearestDistance = Math.abs(getBoxTop(messageBoxes[nearestIndex]) - viewportTop);
			var distance = Math.abs(getBoxTop(messageBox) - viewportTop);
			return distance < nearestDistance ? index : nearestIndex;
		}, 0);
	}

	function isMessageInView() {
		var rect = message.getBoundingClientRect();
		return rect.top < window.innerHeight && rect.bottom > 0;
	}

	function getTargetIndex(direction) {
		var currentIndex = getCurrentIndex();
		return direction > 0 ? currentIndex + 1 : currentIndex - 1;
	}

	function canSnapToIndex(index) {
		return index >= 0 && index < messageBoxes.length;
	}

	function snapToIndex(index) {
		if (isSnapping) return;

		var targetBox = messageBoxes[index];
		if (!targetBox) return;

		var targetTop = getBoxTop(targetBox);
		if (Math.abs(window.pageYOffset - targetTop) < 2) return;

		isSnapping = true;
		wheelAmount = 0;
		touchStartY = null;

		gsap.killTweensOf(window);
		gsap.to(window, {
			duration: SNAP_DURATION,
			ease: SNAP_EASE,
			scrollTo: {
				y: targetTop,
				autoKill: false,
			},
			onComplete: function () {
				isSnapping = false;
			},
			onInterrupt: function () {
				isSnapping = false;
			},
		});
	}

	function handleWheel(event) {
		if (!isMessageInView()) return;

		if (isSnapping) {
			event.preventDefault();
			return;
		}

		var delta = event.deltaY;
		if (Math.abs(delta) < 1) return;

		if (wheelAmount * delta < 0) wheelAmount = 0;
		wheelAmount += delta;

		var direction = wheelAmount > 0 ? 1 : -1;
		var targetIndex = getTargetIndex(direction);
		if (!canSnapToIndex(targetIndex)) {
			wheelAmount = 0;
			return;
		}

		event.preventDefault();

		if (Math.abs(wheelAmount) >= WHEEL_SNAP_THRESHOLD) {
			snapToIndex(targetIndex);
		}
	}

	function handleTouchStart(event) {
		if (!isMessageInView() || !event.touches.length) return;
		touchStartY = event.touches[0].clientY;
	}

	function handleTouchMove(event) {
		if (!isMessageInView() || touchStartY === null || !event.touches.length) return;

		if (isSnapping) {
			event.preventDefault();
			return;
		}

		var delta = touchStartY - event.touches[0].clientY;
		if (Math.abs(delta) < 1) return;

		var direction = delta > 0 ? 1 : -1;
		var targetIndex = getTargetIndex(direction);
		if (!canSnapToIndex(targetIndex)) return;

		event.preventDefault();

		if (Math.abs(delta) >= TOUCH_SNAP_THRESHOLD) {
			snapToIndex(targetIndex);
		}
	}

	function handleTouchEnd() {
		touchStartY = null;
	}

	window.addEventListener("wheel", handleWheel, { passive: false });
	window.addEventListener("touchstart", handleTouchStart, { passive: true });
	window.addEventListener("touchmove", handleTouchMove, { passive: false });
	window.addEventListener("touchend", handleTouchEnd);
}

if (ENABLE_GSAP_MESSAGE_SNAP) {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initMessageSnap);
	} else {
		initMessageSnap();
	}
}

function initLetterReadingHighlight() {
	var letter = document.querySelector(LETTER_SELECTOR);
	if (!letter) return;

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		letter.classList.add(LETTER_VISIBLE_CLASS);
		return;
	}

	var readingLine = parseFloat(
		window.getComputedStyle(document.documentElement).getPropertyValue("--letter-reading-line")
	) || 0.7;
	var targets = Array.prototype.slice.call(document.querySelectorAll(LETTER_SPLIT_SELECTOR));
	if (!targets.length) return;

	var sign = letter.querySelector(LETTER_SIGN_SELECTOR);
	var signTargets = sign ? targets.filter(function (element) {
		return sign.contains(element);
	}) : [];
	var signInkTargets = sign ? Array.prototype.slice.call(sign.querySelectorAll(INK_BLEED_SELECTOR)) : [];
	var signInkTimer = null;
	var signInkPlayed = false;

	targets.forEach(function (element) {
		var text = element.textContent;
		var fragment = document.createDocumentFragment();

		element.textContent = "";
		element.setAttribute("aria-label", text);

		Array.prototype.forEach.call(text, function (character) {
			var span = document.createElement("span");
			span.textContent = character;
			span.setAttribute("aria-hidden", "true");
			fragment.appendChild(span);
		});

		element.appendChild(fragment);
	});

	var litCount = new WeakMap();

	function updateSignInk() {
		if (!signTargets.length || !signInkTargets.length || signInkPlayed) return;

		var isComplete = signTargets.every(function (element) {
			return (litCount.get(element) || 0) >= element.children.length;
		});

		if (!isComplete) {
			if (signInkTimer !== null) {
				window.clearTimeout(signInkTimer);
				signInkTimer = null;
			}
			return;
		}

		if (signInkTimer !== null) return;

		signInkTimer = window.setTimeout(function () {
			signInkTargets.forEach(function (target) {
				target.classList.add(INK_BLEED_PLAY_CLASS);
			});
			signInkPlayed = true;
			signInkTimer = null;
		}, SIGN_INK_DELAY);
	}

	function update() {
		var zone = window.innerHeight * readingLine;
		var dimZone = window.innerHeight * LETTER_DIM_LINE;

		targets.forEach(function (element) {
			var rect = element.getBoundingClientRect();
			if (rect.top > window.innerHeight * 1.5) return;

			element.classList.toggle(LETTER_DIM_CLASS, rect.top <= dimZone);

			var spans = element.children;
			var count;

			if (rect.bottom < 0) {
				count = spans.length;
			} else {
				var ratio = (zone - rect.top) / rect.height;
				ratio = Math.max(0, Math.min(1, ratio));
				count = Math.floor(ratio * spans.length);
			}

			var previousCount = litCount.get(element) || 0;
			if (count === previousCount) return;

			if (count > previousCount) {
				for (var i = previousCount; i < count; i++) spans[i].classList.add("on");
			} else {
				for (var j = count; j < previousCount; j++) spans[j].classList.remove("on");
			}

			litCount.set(element, count);
		});

		updateSignInk();
	}

	var ticking = false;
	function onScroll() {
		if (ticking) return;
		ticking = true;
		window.requestAnimationFrame(function () {
			update();
			ticking = false;
		});
	}

	window.addEventListener("scroll", onScroll, { passive: true });
	window.addEventListener("resize", onScroll, { passive: true });

	if ("IntersectionObserver" in window) {
		var observer = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) return;
				letter.classList.add(LETTER_VISIBLE_CLASS);
				update();
				observer.disconnect();
			});
		}, {
			threshold: 0.08,
		});

		observer.observe(letter);
	} else {
		letter.classList.add(LETTER_VISIBLE_CLASS);
	}

	update();
}

function initStatementGlow() {
	var statement = document.querySelector(STATEMENT_SELECTOR);
	if (!statement) return;

	var anchor = statement.querySelector(STATEMENT_GLOW_ANCHOR_SELECTOR);
	if (!anchor) return;

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
	if (window.matchMedia("(max-width: 798px)").matches) return;

	var targetX = 0;
	var targetY = 0;
	var x = 0;
	var y = 0;
	var frameId = null;
	var isInView = !("IntersectionObserver" in window);

	function stopTick() {
		if (frameId === null) return;
		window.cancelAnimationFrame(frameId);
		frameId = null;
	}

	function scheduleTick() {
		if (!isInView || frameId !== null) return;
		frameId = window.requestAnimationFrame(tick);
	}

	function tick() {
		frameId = null;
		x += (targetX - x) * STATEMENT_GLOW_EASE;
		y += (targetY - y) * STATEMENT_GLOW_EASE;

		if (Math.abs(targetX - x) < STATEMENT_GLOW_REST_DISTANCE) x = targetX;
		if (Math.abs(targetY - y) < STATEMENT_GLOW_REST_DISTANCE) y = targetY;

		anchor.style.transform = "translate3d(" + x + "px, " + y + "px, 0)";

		if (x !== targetX || y !== targetY) scheduleTick();
	}

	statement.addEventListener("pointermove", function (event) {
		var rect = statement.getBoundingClientRect();
		targetX = (event.clientX - (rect.left + rect.width / 2)) * STATEMENT_GLOW_FOLLOW;
		targetY = (event.clientY - (rect.top + rect.height / 2)) * STATEMENT_GLOW_FOLLOW;
		scheduleTick();
	});

	statement.addEventListener("pointerleave", function () {
		targetX = 0;
		targetY = 0;
		scheduleTick();
	});

	if ("IntersectionObserver" in window) {
		var observer = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				isInView = entry.isIntersecting;

				if (isInView) {
					scheduleTick();
				} else {
					stopTick();
				}
			});
		}, {
			threshold: 0,
		});

		observer.observe(statement);
	}

	window.addEventListener("pagehide", function () {
		stopTick();
	}, { once: true });
}

function initStatementReveal() {
	var statement = document.querySelector(STATEMENT_SELECTOR);
	if (!statement) return;

	function reveal() {
		statement.classList.add(STATEMENT_VISIBLE_CLASS);
	}

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		reveal();
		return;
	}

	if ("IntersectionObserver" in window) {
		var observer = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) return;
				reveal();
				observer.disconnect();
			});
		}, {
			threshold: 0.45,
		});

		observer.observe(statement);
	} else {
		reveal();
	}
}

function initScheduleReveal() {
	var schedule = document.querySelector(SCHEDULE_SELECTOR);
	if (!schedule) return;

	function reveal() {
		schedule.classList.add(SCHEDULE_VISIBLE_CLASS);
	}

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		reveal();
		return;
	}

	if ("IntersectionObserver" in window) {
		var observer = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) return;
				reveal();
				observer.disconnect();
			});
		}, {
			threshold: 0.5,
		});

		observer.observe(schedule);
	} else {
		reveal();
	}
}

function initInkBleed() {
	var targets = Array.prototype.slice.call(document.querySelectorAll(INK_BLEED_SELECTOR));
	if (!targets.length) return;

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		targets.forEach(function (target) {
			target.classList.add(INK_BLEED_PLAY_CLASS);
		});
	}
}

function initHeroBrushWipe() {
	var targets = Array.prototype.slice.call(document.querySelectorAll(HERO_BRUSH_SELECTOR));
	if (!targets.length) return;

	function play() {
		targets.forEach(function (target) {
			target.classList.add(HERO_BRUSH_PLAY_CLASS);
		});
	}

	if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
		play();
		return;
	}

	function playAfterDelay() {
		window.setTimeout(play, HERO_BRUSH_LOAD_DELAY);
	}

	if (document.readyState === "complete") {
		playAfterDelay();
	} else {
		window.addEventListener("load", playAfterDelay, { once: true });
	}
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", function () {
		initHeroBrushWipe();
		initLetterReadingHighlight();
		initStatementReveal();
		initScheduleReveal();
		initStatementGlow();
		initInkBleed();
	});
} else {
	initHeroBrushWipe();
	initLetterReadingHighlight();
	initStatementReveal();
	initScheduleReveal();
	initStatementGlow();
	initInkBleed();
}
